import { cn } from '@askable/ui/lib/utils';
import { useFeatureFlags } from 'feature-flags';
import { ChevronLeft } from 'lucide-react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'src/lib/routing';
import { useProductAccess } from 'src/hooks/useProductAccess';

import { useConnectedClient } from 'context/ConnectedClientContext';
import { teamUtils } from 'lib/teams';

type NavItem = {
  label: string;
  links: {
    href: string;
    label: string;
  }[];
};

export const SettingsNavigation = memo(() => {
  const { details } = useConnectedClient();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const isAdmin = teamUtils.isAdminByRoleId(details?.teamRole);
  const { isRouteAllowed } = useProductAccess();
  const { clients_transcript, moderated_scheduling_enabled } = useFeatureFlags([
    'clients_transcript',
    'moderated_scheduling_enabled',
  ]);
  const tagsLinks = clients_transcript ? [{ href: '/settings/tags', label: t('sections.tags.title') }] : [];

  const navItems: NavItem[] = [];

  const accountLinks = [
    {
      href: '/settings/account',
      label: t('sections.settings.profile.title'),
    },
    {
      href: '/settings/notifications',
      label: t('sections.settings.notifications.title'),
    },
    ...(moderated_scheduling_enabled
      ? [
          {
            href: '/settings/integrations',
            label: t('sections.settings.integrations.title'),
          },
        ]
      : []),
  ].filter(link => isRouteAllowed(link.href));

  if (accountLinks.length > 0) {
    navItems.push({
      label: t('sections.settings.account.title'),
      links: accountLinks,
    });
  }

  const isBillingTracksEnabled = details?.ConnectedTeam?.settings?.billing?.tracks.enabled;

  if (!details?.type?.researcher) {
    const links = [
      {
        href: '/settings/members',
        label: t('sections.settings.team.members'),
      },
      {
        href: '/settings/billing',
        label: t('sections.settings.billing.title'),
      },
      ...(!isBillingTracksEnabled
        ? [
            {
              href: '/settings/usage',
              label: t('sections.settings.usage.title'),
            },
            {
              href: '/settings/invoices',
              label: t('sections.settings.invoices.title'),
            },
          ]
        : []),

      {
        href: '/settings/resources',
        label: t('sections.settings.resources.title'),
      },
      ...tagsLinks,
      ...(isAdmin
        ? [
            {
              href: '/settings/sso',
              label: t('sections.settings.team.enterpriseSso.title'),
            },
          ]
        : []),
    ];
    if (isAdmin) {
      links.splice(0, 0, {
        href: '/settings/team',
        label: t('sections.settings.team.general'),
      });
    }

    const filteredLinks = links.filter(link => isRouteAllowed(link.href));

    if (filteredLinks.length > 0) {
      navItems.push({
        label: t('sections.settings.team.title'),
        links: filteredLinks,
      });
    }
  } else {
    const filteredLinks = tagsLinks.filter(link => isRouteAllowed(link.href));
    if (filteredLinks.length > 0) {
      navItems.push({ label: t('sections.settings.team.title'), links: filteredLinks });
    }
  }

  return (
    <>
      <nav className="relative flex w-64 flex-1 flex-col gap-4 overflow-hidden px-3 opacity-100 transition-all duration-150 starting:opacity-0">
        <Link
          to="/"
          // h-7 and px-2 put this on the team switcher's line: settings swaps the
          // whole sidebar header out for this row, so it has to land where the
          // Askable mark and team name were rather than starting its own rhythm.
          className="flex h-7 items-center gap-1 rounded-sm px-2 font-semibold transition-colors hover:bg-foreground/[0.04] focus:bg-foreground/[0.04]"
        >
          <ChevronLeft className="size-4" />
          {t('sections.navigation.backToApp')}
        </Link>

        <ul className="flex flex-col gap-4 overflow-auto">
          {navItems.map(section => (
            <li key={section.label}>
              <h2 className="px-2 pb-2 text-xs text-muted-foreground">{section.label}</h2>

              {section.links ? (
                <ul className="flex flex-1 flex-col gap-0.5 overflow-auto">
                  {section.links.map(link => {
                    const isActive = link.href === pathname;
                    return (
                      <li key={link.label}>
                        <Link
                          to={link.href ?? ''}
                          className={cn(
                            'flex h-7 items-center rounded-sm px-2 text-[13px] font-medium text-nav-foreground transition-colors hover:bg-foreground/[0.04] hover:text-foreground focus:bg-foreground/[0.04] focus:text-foreground dark:hover:bg-foreground/[0.065] dark:focus:bg-foreground/[0.065]',
                            {
                              'bg-foreground/[0.065] text-foreground hover:bg-foreground/[0.065] dark:bg-foreground/[0.11] dark:hover:bg-foreground/[0.11]':
                                isActive,
                            },
                          )}
                        >
                          <div className="truncate">{link.label}</div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      </nav>

      <footer className="flex items-center justify-between gap-4 p-4 pt-6 text-xs text-muted-foreground">
        <div>Version {import.meta.env.VITE_APP_VERSION}</div>

        <a
          href="https://www.askable.com/legal?tab=terms"
          className="underline hover:text-foreground focus:text-foreground"
          rel="noopener noreferrer"
          target="_blank"
        >
          {t('sections.settings.legal')}
        </a>
      </footer>
    </>
  );
});

SettingsNavigation.displayName = 'SettingsNavigation';
